import axios from 'axios';

// A one-time push of what this browser has to the shared library.
//
// The manuals lived in IndexedDB and their answers in localStorage. Without this, the
// day the library moved to the server was the day every manual anyone had read, and
// every phase anyone had run, stopped existing — the data would still be on the disk,
// reachable by nothing.
//
// The ids do not survive: a local id was a random UUID, a shared one is the file's
// SHA-256. So each file is uploaded, the server answers with its real id, and this
// re-keys that document's localStorage answers onto it. That is the whole job, and it
// is why this cannot be a server-side script — the data is in a browser.
//
// Runs once per browser, then never again — but it is safe if it does run twice, and
// that is deliberate rather than lucky. Uploading de-dupes on the hash, and the state
// goes into a NEW RUN of its own: whatever is on the server keeps its runs, this
// laptop's memories become one more, and the pager on Phase 1 shows both. There is no
// version of this that overwrites somebody's work.

// v2, because v1 ran and silently moved nothing. The state PUT had no `run` on it —
// runs arrived two commits after this file — so the route rejected every one of them
// with a 400 and the catch below swallowed it. The manuals uploaded (a different
// route) and every answer about them was dropped on the floor: a library of PDFs with
// no runs, which is exactly what it looked like.
//
// Bumping the key is what lets it try again on a browser that thinks it is done.
const DONE_KEY = 'notice.migratedToServer.v2';
const DB_NAME = 'notice-ai';
const STORE = 'files';

type LocalFile = { id: string; name: string; type: string; addedAt: number; data: Blob };

// The old IndexedDB, opened read-only and WITHOUT a version, so this can never
// trigger an upgrade of a schema nothing writes any more. Resolves to [] when there
// is no database, which is every browser but the two that were using the app.
function readLocalFiles(): Promise<LocalFile[]> {
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME);
    } catch {
      resolve([]);
      return;
    }
    req.onerror = () => resolve([]);
    req.onsuccess = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.close();
        resolve([]);
        return;
      }
      try {
        const all = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
        all.onsuccess = () => {
          resolve((all.result as LocalFile[]) || []);
          db.close();
        };
        all.onerror = () => {
          resolve([]);
          db.close();
        };
      } catch {
        db.close();
        resolve([]);
      }
    };
  });
}

// Every localStorage key belonging to one document, as the state blob the server
// takes. The keys are "notice.<something>.<docId>" — the docId is the tail, and the
// key kept on the server is the same string with the NEW id in place of the old, so
// the stores go on reading exactly what they wrote.
function localStateFor(oldId: string, newId: string): Record<string, unknown> {
  const state: Record<string, unknown> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith('notice.') || !k.endsWith(`.${oldId}`)) continue;
    const raw = localStorage.getItem(k);
    if (raw === null) continue;
    try {
      state[`${k.slice(0, -oldId.length)}${newId}`] = JSON.parse(raw);
    } catch {
      // A key that isn't JSON was never one of ours (the resume point is a bare
      // number, and it is written as one). Send it as the string it is.
      state[`${k.slice(0, -oldId.length)}${newId}`] = raw;
    }
  }
  return state;
}

// Push this browser's library to the server, once. Never throws and never blocks: the
// app works either way — an empty shared library is a library you can upload to.
//
// The flag is only set when EVERY manual made it. v1 set it come what may, so a run
// that moved nothing still counted as done, and the failure was invisible twice over:
// silent in the console and permanent in localStorage. A manual that keeps failing
// costs a retry per page load, which is the cheaper of the two mistakes.
export async function migrateLocalLibrary(): Promise<void> {
  if (localStorage.getItem(DONE_KEY)) return;
  let files: LocalFile[] = [];
  try {
    files = await readLocalFiles();
  } catch {
    files = [];
  }
  if (!files.length) {
    // Nothing to move: this browser never held a library. Flag it anyway, so the
    // IndexedDB open doesn't happen on every load forever.
    localStorage.setItem(DONE_KEY, '1');
    return;
  }
  let moved = 0;
  for (const f of files) {
    try {
      const form = new FormData();
      form.append('file', new File([f.data], f.name || 'manual.pdf', {
        type: f.type || 'application/pdf',
      }));
      const { data } = await axios.post<{ id: string }>('/api/notice/files', form);
      const newId = data?.id;
      if (!newId) continue;
      const state = localStateFor(f.id, newId);
      if (Object.keys(state).length) {
        // Its own run. Answers have to live in one — that is the whole shape of the
        // store now — and a new one means this cannot land on top of anything the
        // other admin has already done to the same manual.
        const { data: run } = await axios.post<{ id: string }>(
          `/api/notice/doc/${encodeURIComponent(newId)}/runs`,
        );
        if (!run?.id) continue;
        await axios.put(`/api/notice/doc/${encodeURIComponent(newId)}/state`, {
          state,
          run: run.id,
        });
      }
      moved += 1;
    } catch (e) {
      // Skip this one and keep going: one unreadable manual must not strand the rest.
      // But SAY so. This catch is the reason v1's failure went unseen for two commits
      // — it swallowed a 400 from every single file and reported success.
      // eslint-disable-next-line no-console
      console.warn(`[notice] could not migrate "${f.name}":`, e);
    }
  }
  // eslint-disable-next-line no-console
  console.info(`[notice] migrated ${moved}/${files.length} local manual(s) to the shared library`);
  if (moved === files.length) localStorage.setItem(DONE_KEY, '1');
}
